import React, { useState, useCallback, useRef, useEffect } from "react"
import PropTypes from "prop-types"
import ReactCrop from "react-image-crop"
import "react-image-crop/dist/ReactCrop.css"
import dragIndicator from "@/public/images/drag_indicator.svg"
import tickButton from "@/public/images/tickButton.svg"
import crossButton from "@/public/images/crossButton.svg"
import { getTranslations } from "@/components/VisualSearch/localization"
import { useTranslation } from "next-i18next"

const ImageCropPicker = props => {
  const { loading, imageData } = props
  const { t } = useTranslation()
  const { tooltip, toolTipCrop } = getTranslations(t)
  const { imageUrl, product, setOutput, output } = props
  const { model1, model2 } = product || {}
  const { xmin, ymin, xmax, ymax } = model1 || {}
  const initalCrop = {
    unit: "%",
    x: 25,
    y: 25,
    width: 40,
    height: 40,
  }
  const initalPredictionDataSet = {
    unit: "%",
    x: (xmin / imageData.width) * 100,
    y: (ymin / imageData.height) * 100,
    width: ((xmax - xmin) / imageData.width) * 100,
    height: ((ymax - ymin) / imageData.height) * 100,
  }

  const initalSet = model2 === null ? initalPredictionDataSet : initalCrop
  const cropInitalSet =
    output?.cropData !== undefined ? output?.cropData : initalSet

  const imgRef = useRef(null)
  const [crop, setCrop] = useState(cropInitalSet)
  const [completedCrop, setCompletedCrop] = useState(null)
  const [showTooltip, setShowTooltip] = useState(
    model2 === null ? "firstText" : "secondText"
  )
  const [showButton, setShowButton] = useState(true)
  const [outputFlag, setOutputFlag] = useState(false)
  const [windowWidth, setWindowWidth] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  })

  const onLoad = useCallback(img => {
    imgRef.current = img
  }, [])

  useEffect(() => {
    if (crop?.x !== props?.output?.cropData) {
      setCrop(cropInitalSet)
    }
  }, [output?.cropData, outputFlag])

  const handleWindowResize = () => {
    if (window?.innerWidth !== windowWidth?.width) {
      setCrop(cropInitalSet)
      setWindowWidth({
        width: window.innerWidth,
        height: window.innerHeight,
      })
    }
  }
  useEffect(() => {
    handleWindowResize()
    window.addEventListener("resize", handleWindowResize)
    return () => {
      window.removeEventListener("resize", handleWindowResize)
    }
  }, [])

  const createOutputBlob = canvas => {
    canvas.toBlob(blob => {}, "image/png", 1)
  }

  const createOutputCanvas = () => {
    const image = imgRef.current
    const canvas = document.createElement("canvas")
    const crop = completedCrop
    const scaleX = image.naturalWidth / image.width
    const scaleY = image.naturalHeight / image.height
    const ctx = canvas.getContext("2d")
    const pixelRatio = window.devicePixelRatio
    canvas.width = crop.width * pixelRatio * scaleX
    canvas.height = crop.height * pixelRatio * scaleY
    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0)
    ctx.imageSmoothingQuality = "high"
    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width * scaleX,
      crop.height * scaleY
    )
    return canvas
  }

  useEffect(() => {
    if (!completedCrop || !imgRef.current) {
      return
    }
    const canvas = createOutputCanvas()
    createOutputBlob(canvas)
  }, [completedCrop])

  const handleOnDragStart = () => {
    setShowTooltip(null)
    setShowButton(true)
  }
  const handleDragEnd = () => {
    if (showTooltip === null) {
      setShowButton(false)
    }
  }

  const handleBoundingBox = flags => {
    const { width, height } = imageData
    setOutputFlag(!outputFlag)
    if (!flags) {
      setCompletedCrop(cropInitalSet)
      setShowButton(true)
      return
    }
    const calcData = {}
    const calcImageWidthHeight =
      document.getElementsByClassName("ReactCrop__image")[0]
    calcData.xmax =
      (completedCrop.x + completedCrop.width) / calcImageWidthHeight.clientWidth
    calcData.ymax =
      (completedCrop.y + completedCrop.height) /
      calcImageWidthHeight.clientHeight
    calcData.xmin = completedCrop.x / calcImageWidthHeight.clientWidth
    calcData.ymin = completedCrop.y / calcImageWidthHeight.clientHeight

    const predictionDataPx = {}
    predictionDataPx.xmax = (calcData.xmax * width).toFixed(4)
    predictionDataPx.ymax = (calcData.ymax * height).toFixed(4)
    predictionDataPx.xmin = (calcData.xmin * width).toFixed(4)
    predictionDataPx.ymin = (calcData.ymin * height).toFixed(4)
    setOutput({
      predictionData: predictionDataPx,
      cropData: completedCrop,
      flag: flags,
    })
    setShowButton(true)
  }

  const onCropComplete = crop => {
    if (isNaN(crop.x)) {
      crop.x = 0
    }
    if (isNaN(crop.y)) {
      crop.y = 0
    }
    setCrop(crop)
  }

  return (
    <>
      <div className="cropper-section">
        <ReactCrop
          aspects={1}
          crop={crop}
          keepSelection
          src={imageUrl}
          onChange={c => onCropComplete(c)}
          minHeight={55}
          minWidth={55}
          onImageLoaded={onLoad}
          disabled={!loading}
          locked={!loading}
          onDragStart={handleOnDragStart}
          onDragEnd={handleDragEnd}
          onComplete={c => setCompletedCrop(c)}
          alt="crop"
          data-testid="image-react-crop-picker"
          renderSelectionAddon={() => (
            <div>{<img src={dragIndicator?.src} alt="drag_indicator" />}</div>
          )}
        />
      </div>
      {showTooltip !== null ? (
        <>
          {showTooltip === "firstText" ? (
            <div className="coachBox position-absolute text-center">
              <p>{tooltip}</p>
            </div>
          ) : null}

          {showTooltip === "secondText" ? (
            <div className="coachBox secondText position-absolute text-center">
              <p>{toolTipCrop}</p>
            </div>
          ) : null}
        </>
      ) : null}

      {!showButton && (
        <div className="verify-image position-absolute">
          <div className="icon">
            <img
              src={tickButton?.src}
              alt="correct"
              onClick={() => handleBoundingBox(true)}
            />
          </div>
          <div className="icon mx-3">
            <img
              src={crossButton?.src}
              alt="cross"
              onClick={() => handleBoundingBox(false)}
            />
          </div>
        </div>
      )}
    </>
  )
}
ImageCropPicker.propTypes = {
  loading: PropTypes.bool.isRequired,
  imageData: PropTypes.shape({
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
  }).isRequired,
  imageUrl: PropTypes.string.isRequired,
  product: PropTypes.shape({
    model1: PropTypes.shape({
      xmin: PropTypes.number,
      ymin: PropTypes.number,
      xmax: PropTypes.number,
      ymax: PropTypes.number,
    }),
    model2: PropTypes.any,
  }),
  setOutput: PropTypes.func.isRequired,
  output: PropTypes.shape({
    cropData: PropTypes.object,
  }),
}

export default ImageCropPicker
